import {useRef, useEffect, useMemo, forwardRef} from 'react'
import snakeCase from 'lodash/snakeCase.js'

import {updateForm, useAutoForm, useSelector} from '../../store.js'
import Zropdown, {useZropdown, useZropdownElement} from './Zropdown.js'
import {currentDropdownSelector} from '../../redux/selectors/ui/index.js'
import cn from '../className.js'
import useStable from '../useStable.js'
import {
  Count,
  IfPluralNotZero,
  IfSingle,
  IfZero,
  PluralBlock,
} from './Plural.js'

export function DefaultListItem({
  option,
  index,
  form: {selectedIndex},
  values,
  onSelectInternal,
  children,
}) {
  const ref = useRef()
  const isSelected = index === selectedIndex
  useZropdownElement({ref, isSelected})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <button
        className={cn`btn--link list__link--dropdown ${{
          selected: values.includes(option.value),
          'list__link--dropdown-arrow-focus': isSelected,
        }}`}
        type="button"
        onClick={() => onSelectInternal(option)}
        disabled={option.disabled}
      >
        {children || option.display}
      </button>
    </li>
  )
}

export function CheckboxListItem({
  option,
  index,
  form: {selectedIndex},
  values,
  onSelectInternal,
}) {
  const ref = useRef()
  const isSelected = index === selectedIndex
  useZropdownElement({ref, isSelected})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown"
      key={option.value}
    >
      <label
        className={`label--selectable label--sm flex margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isSelected,
        }}`}
        data-dropdown-prevent-close
      >
        <span>
          <input
            className="fs-00"
            type="checkbox"
            checked={values.includes(option.value)}
            onChange={() => onSelectInternal(option)}
            disabled={option.disabled}
          />
        </span>
        <span className="margin-left-5">{option.display}</span>
      </label>
    </li>
  )
}

export function ItemSeparator({option, isFocused}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown list__item--dropdown-header list__item--dropdown-header-pkg-type"
    >
      <span
        className={cn`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset align-items-center  margin-bottom-0"
          htmlFor={option.value}
          data-dropdown-prevent-close
        >
          {option.display}
        </label>
      </span>
    </li>
  )
}

/**
 * The visual button of the custom Select
 *
 * @param {Object} props
 * @param {String} props.dropdown
 * @param {String} [props.className]
 * @param {String} [props.placeholder]
 * @param {Bool} [props.disabled]
 * @param {String} [props.errorMessage]
 * @param {Object[]} [props.chosenOptions]
 * @param {Function} [props.IfNotSelected]
 * @param {Function} [props.IfSingleSelected]
 * @param {Function} [props.IfMultipleSelected]
 * @returns {ReactNode}
 */
export const SelectButton = forwardRef(
  (
    {
      dropdown,
      placeholder,
      disabled,
      className,
      errorMessage,
      chosenOptions,
      IfNotSelected,
      IfSingleSelected,
      IfMultipleSelected,
    },
    ref,
  ) => {
    IfNotSelected = IfNotSelected || (() => <em>{placeholder}</em>)
    IfSingleSelected = IfSingleSelected || (({option}) => option.display)
    IfMultipleSelected =
      IfMultipleSelected ||
      (() => (
        <>
          <Count /> Selected
        </>
      ))

    return (
      <button
        id={dropdown}
        ref={ref}
        className={cn`btn btn--dropdown btn--filter btn--filter-w-border ${className}`}
        type="button"
        disabled={disabled}
      >
        <span
          className={cn`btn__text btn__text--zelect ${{
            'error-message': errorMessage,
          }}`}
        >
          <PluralBlock array={chosenOptions}>
            <IfZero>
              <IfNotSelected />
            </IfZero>
            <IfSingle>
              <IfSingleSelected option={chosenOptions[0]} />
            </IfSingle>
            <IfPluralNotZero>
              <IfMultipleSelected chosenOptions={chosenOptions} />
            </IfPluralNotZero>
          </PluralBlock>
        </span>
      </button>
    )
  },
)
SelectButton.displayName = 'SelectButton'

function buildOptions(
  options,
  values,
  addEmptyOption,
  emptyOptionDisplay,
  onInvalidOption,
  isMultiSelect,
  hideDisabled,
) {
  if (addEmptyOption) {
    options = [
      {value: '', display: emptyOptionDisplay, isEmptyOption: true},
      ...options,
    ]
  }

  if (hideDisabled) {
    options = options.filter((option) => {
      if (option.disabled) {
        return values.includes(option.value)
      }

      return true
    })
  }

  if (onInvalidOption) {
    const disabled = !isMultiSelect

    const invalidOptions = []
    for (const value of values) {
      if (!options.find((option) => option.value === value)) {
        invalidOptions.push({
          value,
          display: value,
          disabled,
          ...onInvalidOption(value),
        })
      }
    }

    if (invalidOptions.length) {
      options = [...invalidOptions, ...options]
    }
  }

  return options
}

/**
 * A Custom Dropdown that behaves like a native select element
 *
 * @param {Object} props
 * @param {Array} props.options
 * @param {Bool} [props.required]
 * @param {String} [props.errorMessage]
 * @param {String} [props.id]
 * @param {*} [props.value]
 * @param {ReactNode} [props.label]
 * @param {String} [props.labelClassName]
 * @param {String} [props.dropdownClassName]
 * @param {String} [props.buttonClassName]
 * @param {Function} props.onChange
 * @param {ReactNode} [props.header]
 * @param {Function} [props.OptionComponent]
 * @param {Function} [props.IfNotSelected]
 * @param {Function} [props.IfSingleSelected]
 * @param {Function} [props.IfMultipleSelected]
 * @param {ReactNode} [props.footer]
 * @param {Bool} [props.addEmptyOption]
 * @param {ReactNode} [props.emptyOptionDisplay]
 * @param {Function} [props.onInvalidOption]
 * @param {String} [props.placeholder]
 * @param {Bool} [props.disabled]
 * @param {Bool} [props.hideDisabled]
 * @returns {ReactNode}
 */
export default function Zelect({
  options,
  required,
  errorMessage,
  id,
  value,
  label,
  labelClassName,
  dropdownClassName,
  buttonClassName,
  onChange,
  header,
  OptionComponent,
  IfNotSelected,
  IfSingleSelected,
  IfMultipleSelected,
  footer,
  addEmptyOption,
  emptyOptionDisplay = <>&nbsp;</>,
  onInvalidOption,
  placeholder = 'Select an option',
  disabled,
  hideDisabled,
}) {
  id = id || (label ? snakeCase(label) : 'NOT_DEFINED')
  const dropdown = `SELECT__${id.toUpperCase()}`

  value = useStable(value)
  const isMultiSelect = Array.isArray(value)

  const values = useMemo(
    () => (isMultiSelect ? value : [value]),
    [isMultiSelect, value],
  )

  options = useStable(options)
  options = useMemo(
    () =>
      buildOptions(
        options,
        values,
        addEmptyOption,
        emptyOptionDisplay,
        onInvalidOption,
        isMultiSelect,
        hideDisabled,
      ),
    [options, values, addEmptyOption, isMultiSelect],
  )

  const chosenOptions = useMemo(
    () => options.filter((option) => values.includes(option.value)),
    [options, values],
  )

  const form = useAutoForm(dropdown, {
    results: [],
    selectedIndex: 0,
  })

  useEffect(() => {
    const firstSelectedOptionIndex = options.indexOf(chosenOptions[0])

    updateForm(dropdown, {
      results: options,
      selectedIndex:
        firstSelectedOptionIndex !== -1 ? firstSelectedOptionIndex : 0,
    })
  }, [options, values, chosenOptions])

  const buttonRef = useRef()

  const {onConfirm, onSelectInternal, onUp, onDown} = useZropdown({
    dropdown,
    form,
    onSelect: onChange,
    closeOnConfirm: !isMultiSelect,
  })
  const currentDropdown = useSelector(currentDropdownSelector)

  OptionComponent = OptionComponent || DefaultListItem

  return (
    <>
      {label && (
        <label className={cn`label--bold ${labelClassName}`} htmlFor={dropdown}>
          <span>{label}</span>
          {required && <span className="required">*</span>}
        </label>
      )}
      <div
        className={cn`wrap-outer--tag-dropdown-with-filter ${{
          open: currentDropdown === dropdown,
        }} ${dropdownClassName}`}
        data-dropdown={dropdown}
      >
        <SelectButton
          ref={buttonRef}
          dropdown={dropdown}
          className={buttonClassName}
          placeholder={placeholder}
          disabled={disabled}
          errorMessage={errorMessage}
          chosenOptions={chosenOptions}
          IfNotSelected={IfNotSelected}
          IfSingleSelected={IfSingleSelected}
          IfMultipleSelected={IfMultipleSelected}
        />
        <Zropdown
          dropdown={dropdown}
          parentRef={buttonRef}
          onConfirm={onConfirm}
          onUp={onUp}
          onDown={onDown}
          header={header}
          footer={footer}
          blurOnOpen
        >
          {form.results.map((option, index) => (
            <OptionComponent
              key={option.value}
              option={option}
              index={index}
              form={form}
              values={values}
              onSelectInternal={onSelectInternal}
            />
          ))}
        </Zropdown>
      </div>
      {errorMessage && (
        <small className="error error-message">{errorMessage}</small>
      )}
    </>
  )
}
